import { useAuth } from 'lib/auth';
import { useRouter } from 'next/router';
import { createContext, useContext } from 'react';
import useEngagementBasedPopup from './useEngagementBasedPopup';
import { INCLUDED_TIMED_POPUP_PATHS } from './constants';
import { useCart } from 'lib/cart';
import usePostAddToCartPopup from './usePostAddToCartPopup';
import useReferralIncentivePopup from './useReferralIncentivePopup';

interface PopupContextValue {
  showTimedPopup: boolean;
  trackProductView: (productId: string) => void;
  dismiss: () => void;
  showPostAddToCartPopup: boolean;
  dismissPostAddToCartPopup: () => void;
  triggerPostAddToCartPopup: () => void;
  referralCode: string | null;
  showReferralIncentivePopup: boolean;
  dismissReferralIncentivePopup: () => void;
  snoozeReferralIncentivePopup: () => void;
}

const defaultContextValue: PopupContextValue = {
  showTimedPopup: false,
  trackProductView: () => {},
  dismiss: () => {},
  showPostAddToCartPopup: false,
  dismissPostAddToCartPopup: () => {},
  triggerPostAddToCartPopup: () => {},
  referralCode: null,
  showReferralIncentivePopup: false,
  dismissReferralIncentivePopup: () => {},
  snoozeReferralIncentivePopup: () => {},
};

export const PopupContext = createContext<PopupContextValue>(
  defaultContextValue,
);

export const PopupContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { session, loading } = useAuth();
  const { cart } = useCart();
  const { pathname } = useRouter();

  const isCartEmpty = cart?.quantity === 0;

  const shouldTrackEngagementPopup = INCLUDED_TIMED_POPUP_PATHS.includes(
    pathname,
  );
  const isNewUser = !loading && !session?.user;

  const {
    showPopup: showTimedPopup,
    trackProductView,
    dismiss,
  } = useEngagementBasedPopup({
    shouldTrack: shouldTrackEngagementPopup,
    isCartEmpty,
    isNewUser,
  });

  const {
    dismiss: dismissPostAddToCartPopup,
    showPostAddToCartPopup,
    triggerPopup: triggerPostAddToCartPopup,
  } = usePostAddToCartPopup({
    isNewUser,
    cartQuantity: cart?.quantity,
  });

  const {
    referralCode,
    showPopup: showReferralIncentivePopup,
    dismiss: dismissReferralIncentivePopup,
    rememberMe: snoozeReferralIncentivePopup,
  } = useReferralIncentivePopup({
    key: Boolean(session?.user) ? session?.user?.userId : undefined,
    pathname,
    isLoggedIn: Boolean(session?.user),
  });

  return (
    <PopupContext.Provider
      value={{
        showTimedPopup,
        trackProductView,
        dismiss,
        dismissPostAddToCartPopup,
        triggerPostAddToCartPopup,
        showPostAddToCartPopup,
        referralCode,
        showReferralIncentivePopup,
        dismissReferralIncentivePopup,
        snoozeReferralIncentivePopup,
      }}
    >
      {children}
    </PopupContext.Provider>
  );
};

export const usePopupContext = () => useContext(PopupContext);
